import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { LoadingOverlay } from 'components/core/loading-overlay';
import { useConfig } from 'hooks/use-config';
import { getOidcRuntimeEnv } from 'lib/runtime-env';
import { useMobileDeepLink } from 'hooks/use-mobile-deep-link';
import { MobileDeepLinkLoader } from 'components/core/mobile-deep-link-loader';

export function SigninOidcCallbackPage() {
  const [searchParams] = useSearchParams();
  const { setAuthenticated } = useAuthStore();
  const hasProcessed = useRef(false);
  const { configurationSettings } = useConfig();

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const tenantId = searchParams.get('tenant_id') || process.env.REACT_APP_PUBLIC_BLOCKS_KEY;
  const mobileBundleId = process.env.REACT_APP_PUBLIC_MOBILE_BUNDLE_ID?.trim();

  const mobileParams = new URLSearchParams(searchParams);
  if (!mobileParams.has('tenant_id') && tenantId) mobileParams.set('tenant_id', tenantId);

  const deepLinkUrl = mobileBundleId ? `${mobileBundleId}://login/?${mobileParams.toString()}` : '';
  const { shouldShowFallback, isMobile } = useMobileDeepLink({
    deepLinkUrl,
    fallbackDelay: 1000,
  });
  const shouldUseWebFlow = !isMobile || !mobileBundleId || shouldShowFallback;

  useEffect(() => {
    if (hasProcessed.current || !configurationSettings || !shouldUseWebFlow) {
      return;
    }

    hasProcessed.current = true;

    (async () => {
      const oidcEnv = getOidcRuntimeEnv(configurationSettings.paths.baseUrl);
      const callbackUrl = new URL(`${oidcEnv.idpBaseUrl.replace(/\/$/, '')}/iam/v4/idp/callback`);
      if (code) callbackUrl.searchParams.set('code', code);
      if (state) callbackUrl.searchParams.set('state', state);
      if (error) callbackUrl.searchParams.set('error', error);
      if (tenantId) callbackUrl.searchParams.set('tenant_id', tenantId);

      const headers: Record<string, string> = {};
      if (tenantId) {
        headers['X-Blocks-Key'] = tenantId;
      }
      console.log('[OIDC] Callback URL:', callbackUrl.toString());

      fetch(callbackUrl.toString(), { headers, credentials: 'include' })
        .then((response) => {
          if (!response.ok) {
            window.location.href = '/login?error=callback_failed';
            return;
          }

          setAuthenticated();
          window.location.href = '/';
        })
        .catch(() => {
          window.location.href = '/login?error=callback_error';
        });
    })();
  }, [
    code,
    configurationSettings,
    error,
    setAuthenticated,
    shouldUseWebFlow,
    state,
    tenantId,
  ]);

  if (!shouldUseWebFlow) {
    return <MobileDeepLinkLoader />;
  }

  return <LoadingOverlay />;
}
